import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

export function GuidedIntake({ t, onSubmit, sending, error }) {
  const steps = [
    { key: "company", q: t.intake.q.company, required: true },
    { key: "industry", q: t.intake.q.industry, optional: true },
    { key: "location", q: t.intake.q.location, optional: true },
    { key: "website", q: t.intake.q.website, optional: true },
    { key: "name", q: t.intake.q.name, required: true },
    { key: "role", q: t.intake.q.role, optional: true },
    { key: "contact", q: t.intake.q.contact, contact: true },
    { key: "notes", q: t.intake.q.notes, optional: true, multiline: true },
  ];

  const [idx, setIdx] = useState(0);
  const [values, setValues] = useState({ company: "", industry: "", location: "", website: "", name: "", role: "", email: "", whatsapp: "", notes: "" });
  const [localErr, setLocalErr] = useState("");
  const inputRef = useRef(null);

  const step = steps[idx];
  const isLast = idx === steps.length - 1;

  useEffect(() => {
    setLocalErr("");
    const id = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(id);
  }, [idx]);

  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const validate = () => {
    if (step.contact) {
      if (!values.email.trim() && !values.whatsapp.trim()) {
        setLocalErr(t.intake.requireOne);
        return false;
      }
      return true;
    }
    if (step.required && !values[step.key].trim()) {
      setLocalErr(t.intake.required);
      return false;
    }
    return true;
  };

  const advance = () => {
    if (!validate()) return;
    if (isLast) {
      onSubmit(values);
      return;
    }
    setIdx((i) => i + 1);
  };

  const skip = () => {
    if (isLast) {
      onSubmit(values);
      return;
    }
    setIdx((i) => i + 1);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !(step.multiline && e.shiftKey)) {
      e.preventDefault();
      advance();
    }
  };

  const num = String(idx + 1).padStart(2, "0");
  const total = String(steps.length).padStart(2, "0");

  return (
    <motion.div
      data-testid="guided-intake"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: EASE }}
      className="relative z-10 w-full max-w-xl mx-auto text-center"
    >
      <div className="mb-8 text-xs tracking-[0.25em] text-bh-c2">
        {num} <span className="mx-1">—</span> {total}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step.key}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.5, ease: EASE }}
        >
          {idx === 0 && <p className="mb-5 text-sm text-bh-soft">{t.intake.intro}</p>}
          <h3 className="font-display text-2xl md:text-4xl tracking-tight">{step.q}</h3>

          <div className="mt-10">
            {step.contact ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-left">
                <label className="flex flex-col gap-2">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-bh-soft">{t.contact.email}</span>
                  <input
                    ref={inputRef}
                    data-testid="contact-email"
                    type="email"
                    value={values.email}
                    onChange={(e) => set("email", e.target.value)}
                    onKeyDown={onKey}
                    className="w-full bg-transparent border-b border-bh-line py-2 text-lg outline-none focus:border-bh-ink transition-colors duration-300"
                  />
                </label>
                <label className="flex flex-col gap-2">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-bh-soft">{t.contact.whatsapp}</span>
                  <input
                    data-testid="contact-whatsapp"
                    value={values.whatsapp}
                    onChange={(e) => set("whatsapp", e.target.value)}
                    onKeyDown={onKey}
                    className="w-full bg-transparent border-b border-bh-line py-2 text-lg outline-none focus:border-bh-ink transition-colors duration-300"
                  />
                </label>
              </div>
            ) : step.multiline ? (
              <textarea
                ref={inputRef}
                data-testid={`intake-${step.key}`}
                rows={2}
                value={values[step.key]}
                onChange={(e) => set(step.key, e.target.value)}
                onKeyDown={onKey}
                className="no-scrollbar w-full resize-none bg-transparent border-b border-bh-line py-2 text-center text-xl md:text-2xl outline-none focus:border-bh-ink transition-colors duration-300"
              />
            ) : (
              <input
                ref={inputRef}
                data-testid={`intake-${step.key}`}
                value={values[step.key]}
                onChange={(e) => set(step.key, e.target.value)}
                onKeyDown={onKey}
                className="w-full bg-transparent border-b border-bh-line py-2 text-center text-2xl md:text-3xl outline-none focus:border-bh-ink transition-colors duration-300"
              />
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {(localErr || error) && (
        <p data-testid="intake-error" className="mt-6 text-sm text-bh-c3">
          {error || localErr}
        </p>
      )}

      <div className="mt-10 flex items-center justify-center gap-8">
        {step.optional && !isLast && (
          <button data-testid="intake-skip" onClick={skip} className="text-sm text-bh-c2 hover:text-bh-ink transition-colors">
            {t.intake.skip}
          </button>
        )}
        <button
          data-testid="intake-next-btn"
          onClick={advance}
          disabled={sending}
          className="group inline-flex items-center gap-3 text-lg disabled:opacity-40"
        >
          <span className="relative">
            {t.intake.next}
            <span className="absolute left-0 -bottom-1 h-px w-full bg-bh-ink origin-left scale-x-100 group-hover:scale-x-0 transition-transform duration-500" />
            <span className="absolute left-0 -bottom-1 h-px w-full bg-bh-ink origin-right scale-x-0 group-hover:scale-x-100 transition-transform duration-500 delay-100" />
          </span>
          {!sending && <span className="transition-transform duration-500 group-hover:translate-x-2">→</span>}
        </button>
      </div>
    </motion.div>
  );
}
